import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  TextField,
  LinearProgress,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  IconChevronLeft,
  IconCamera,
  IconBluetooth,
  IconCheck,
  IconX,
  IconPackages,
} from "@tabler/icons-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { markingApi, ozonAssembliesApi } from "../api";
import ManufacturerCatalogPanel, {
  CatalogProductCard,
} from "../components/ManufacturerCatalogPanel";
import {
  groupOzonProductsForCatalog,
  normalizeCatalogsIndex,
  useManufacturerCatalogView,
} from "../utils/assemblyCatalogView";
import {
  bannerSx,
  bannerTextSx,
  bannerTitleSx,
  camera,
  barIconActiveSx,
  barIconSx,
  cabinetChipSx,
  cabinetChipTextSx,
  cardActiveSx,
  cardDoneSx,
  cardSx,
  counterPillSx,
  counterPillTextSx,
  ds,
  dsText,
  mutedTextSx,
  scannerFieldSx,
  sectionLabelSx,
  shellSx,
  softChipSx,
  topBarSx,
} from "../theme/keeperTheme";
import {
  MARKING_ZXING_HINTS,
  focusInputNoScroll,
  getMarkingScanRejectMessage,
  markingCodeForApply,
  remapRuLayoutToEn,
} from "../utils/markingScan";

const CAMERA_H = 200;
const SCANNER_STRIP_H = 88;
const CRPT_DEBOUNCE_CAMERA_MS = 380;
const CRPT_DEBOUNCE_SCANNER_MS = 90;
/** Очередь обновляем сама по себе: её же меняют заказы и другой кладовщик. */
const POLL_LINES_MS = 20000;

function normalizeScan(raw) {
  const s = (raw || "").trim();
  if (!s) return "";
  return /[а-яА-ЯёЁ]/.test(s) ? remapRuLayoutToEn(s) : s;
}

/** Строки сборки → карточки по отправлениям, самый срочный дедлайн первым. */
function groupByPosting(lines) {
  const byPosting = new Map();
  for (const ln of lines || []) {
    const pn = ln.posting_number;
    if (!byPosting.has(pn)) {
      byPosting.set(pn, {
        posting_number: pn,
        shipment_date: ln.shipment_date || null,
        ordered_at: ln.ordered_at || null,
        products: [],
        need: 0,
        done: 0,
      });
    }
    const g = byPosting.get(pn);
    const need = Number(ln.line_qty) || 0;
    const done = Math.min(Number(ln.scanned_qty) || 0, need);
    g.products.push({ ...ln, need, done });
    g.need += need;
    g.done += done;
  }
  return Array.from(byPosting.values()).sort((a, b) =>
    String(a.shipment_date || "9999").localeCompare(String(b.shipment_date || "9999")),
  );
}

function shipmentLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `до ${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function orderedLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `заказ ${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Сборка Ozon: кладовщик сканирует КИЗ в произвольном порядке.
 *
 * Отправление он не сканирует вообще — сервер сам находит товар по GTIN из кода
 * и отдаёт его тому отправлению, у которого ближе дедлайн отгрузки. Когда набор
 * по отправлению собран, коды уходят в Ozon одним запросом, затем сборка и этикетка.
 */
export default function OzonAssembliesStartPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedCabinetId = searchParams.get("cabinet") || "";
  const selectedCabinetName = searchParams.get("cabinet_name") || "";

  const videoRef = useRef(null);
  const scanControlsRef = useRef(null);
  const scannerInputRef = useRef(null);
  const checkTimerRef = useRef(null);
  const lastProcessedScanRef = useRef("");
  const scanInFlightRef = useRef(false);
  const lastPointerDownTargetRef = useRef(null);

  const [inputMode, setInputMode] = useState("scanner");
  const inputModeRef = useRef(inputMode);
  inputModeRef.current = inputMode;

  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [decoderError, setDecoderError] = useState(null);
  const [scannedText, setScannedText] = useState("");

  const [crptLoading, setCrptLoading] = useState(false);
  const [crptPhase, setCrptPhase] = useState("idle");

  const [lines, setLines] = useState([]);
  const [linesLoading, setLinesLoading] = useState(true);
  const [linesError, setLinesError] = useState(null);
  const [remainingUnits, setRemainingUnits] = useState(0);
  const [catalogsIndex, setCatalogsIndex] = useState({});

  const [busyPosting, setBusyPosting] = useState("");
  const [, setLabelUrl] = useState("");
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const [lastPosting, setLastPosting] = useState(null);

  const fetchLines = useCallback(
    async ({ showLoading = false } = {}) => {
      if (!selectedCabinetId) {
        if (showLoading) {
          setLinesLoading(false);
          setLinesError("Не выбран кабинет Ozon. Вернитесь назад и выберите кабинет.");
        }
        return;
      }
      if (showLoading) {
        setLinesLoading(true);
        setLinesError(null);
      }
      try {
        const data = await ozonAssembliesApi.markingLines(selectedCabinetId);
        setLines(Array.isArray(data.lines) ? data.lines : []);
        setRemainingUnits(Number(data.remaining_units) || 0);
        setCatalogsIndex(normalizeCatalogsIndex(data.catalogs_index));
      } catch (e) {
        if (showLoading) setLinesError(e?.message || "Не удалось загрузить очередь сборки");
      } finally {
        if (showLoading) setLinesLoading(false);
      }
    },
    [selectedCabinetId],
  );

  useEffect(() => {
    fetchLines({ showLoading: true });
  }, [fetchLines]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => fetchLines({ showLoading: true }));
  }, [registerToolbarRefresh, fetchLines]);

  useEffect(() => {
    const id = window.setInterval(() => fetchLines({ showLoading: false }), POLL_LINES_MS);
    return () => window.clearInterval(id);
  }, [fetchLines]);

  const postings = useMemo(() => groupByPosting(lines), [lines]);
  const readyPostings = useMemo(
    () => postings.filter((p) => p.need > 0 && p.done >= p.need),
    [postings],
  );
  const catalogProducts = useMemo(() => groupOzonProductsForCatalog(lines), [lines]);
  const manufacturerView = useManufacturerCatalogView(catalogProducts, catalogsIndex);
  const {
    groupByManufacturer,
    setGroupByManufacturer,
    catalogStack,
    currentCatalogId,
    visibleCatalogs,
    filteredIncomplete,
    filteredComplete,
    handleEnterCatalog,
    handleCatalogBreadcrumbClick,
  } = manufacturerView;

  // --- камера ---

  useEffect(() => {
    if (inputMode !== "camera") {
      setCameraReady(false);
      return undefined;
    }
    let stream;
    let videoEl = null;
    let cancelled = false;
    const start = async () => {
      setCameraError(null);
      setCameraReady(false);
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        videoEl = videoRef.current;
        if (videoEl) {
          videoEl.srcObject = stream;
          await videoEl.play().catch(() => {});
          if (!cancelled) setCameraReady(true);
        }
      } catch (e) {
        if (!cancelled) {
          setCameraError(
            e?.name === "NotAllowedError"
              ? "Нет доступа к камере. Разрешите в настройках браузера."
              : e?.message || "Не удалось включить камеру",
          );
        }
      }
    };
    start();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((t) => t.stop());
      if (videoEl) videoEl.srcObject = null;
    };
  }, [inputMode]);

  useEffect(() => {
    if (inputMode !== "camera" || !cameraReady || cameraError) return undefined;
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    let cancelled = false;
    setDecoderError(null);
    const reader = new BrowserMultiFormatReader(MARKING_ZXING_HINTS);
    reader
      .decodeFromVideoElement(videoEl, (result) => {
        if (cancelled || !result) return;
        setScannedText(result.getText());
      })
      .then((controls) => {
        if (cancelled) {
          controls.stop();
          return;
        }
        scanControlsRef.current = controls;
      })
      .catch((e) => {
        if (!cancelled) setDecoderError(e?.message || "Ошибка сканера");
      });
    return () => {
      cancelled = true;
      const c = scanControlsRef.current;
      scanControlsRef.current = null;
      if (c) c.stop();
    };
  }, [inputMode, cameraReady, cameraError]);

  // --- фокус поля BT-сканера ---

  useEffect(() => {
    if (inputMode !== "scanner") return undefined;
    const focus = () => focusInputNoScroll(scannerInputRef.current);
    const t1 = window.setTimeout(focus, 50);
    const t2 = window.setTimeout(focus, 300);
    const onVis = () => {
      if (document.visibilityState === "visible") focus();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [inputMode]);

  useEffect(() => {
    const onPointerDown = (e) => {
      lastPointerDownTargetRef.current = e.target;
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, []);

  const clearScanField = useCallback(() => {
    setScannedText("");
    lastProcessedScanRef.current = "";
    if (inputModeRef.current === "scanner") {
      const inp = scannerInputRef.current;
      if (inp) inp.value = "";
      window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
    }
  }, []);

  // --- скан КИЗ: Честный знак, затем привязка на сервере ---

  useEffect(() => {
    let cancelled = false;
    if (checkTimerRef.current) {
      clearTimeout(checkTimerRef.current);
      checkTimerRef.current = null;
    }
    const raw = normalizeScan(scannedText);
    if (!raw) {
      setCrptPhase("idle");
      setCrptLoading(false);
      return undefined;
    }
    if (scanInFlightRef.current) return undefined;
    if (lastProcessedScanRef.current === raw) return undefined;

    const rejectMsg = getMarkingScanRejectMessage(raw);
    if (rejectMsg) {
      lastProcessedScanRef.current = raw;
      setCrptPhase("error");
      setStatusTone("error");
      setStatusMessage(rejectMsg);
      return undefined;
    }

    setCrptPhase("debounce");
    setStatusMessage(null);
    const debounceMs =
      inputMode === "scanner" ? CRPT_DEBOUNCE_SCANNER_MS : CRPT_DEBOUNCE_CAMERA_MS;

    checkTimerRef.current = setTimeout(() => {
      checkTimerRef.current = null;
      (async () => {
        if (cancelled || scanInFlightRef.current) return;
        lastProcessedScanRef.current = raw;
        scanInFlightRef.current = true;
        setCrptPhase("loading");
        setCrptLoading(true);
        let crpt = null;
        try {
          crpt = await markingApi.trueApiResolve(raw);
        } catch (e) {
          // Честный знак недоступен — код всё равно отдаём серверу, GTIN он возьмёт из Data Matrix
          crpt = null;
        }
        try {
          const res = await ozonAssembliesApi.scan({
            connection_id: Number(selectedCabinetId),
            marking_code: markingCodeForApply(raw),
            crpt_payload: crpt || undefined,
          });
          if (cancelled) return;
          setCrptPhase("ok");
          setStatusTone("success");
          setLastPosting(res.posting_number);
          const scanSummary =
            `${res.title || `SKU ${res.sku}`} → ${res.posting_number} ` +
            `(${res.scanned_qty} из ${res.line_qty})`;
          setStatusMessage(
            scanSummary +
              (res.posting_complete ? " · отправление укомплектовано, передаём КИЗ в Ozon…" : ""),
          );
          void fetchLines({ showLoading: false });
          if (res.posting_complete) {
            const postingNumber = res.posting_number;
            setBusyPosting(postingNumber);
            ozonAssembliesApi
              .applyMarking({
                connection_id: Number(selectedCabinetId),
                posting_number: postingNumber,
              })
              .then((applied) => {
                setStatusTone(applied?.ship_available ? "success" : "warning");
                setStatusMessage(
                  applied?.ship_available
                    ? `Ozon принял ${applied.codes_sent} КИЗ по ${postingNumber}. Можно собирать.`
                    : `КИЗ по ${postingNumber} переданы, Ozon проверяет коды ` +
                        `(статус «${applied?.status || "—"}»). Если не обновится — нажмите «В Ozon» через минуту.`,
                );
              })
              .catch((applyErr) => {
                setStatusTone("error");
                setStatusMessage(
                  `КИЗ по ${postingNumber} не переданы в Ozon: ${applyErr?.message || "ошибка"}. Нажмите «В Ozon».`,
                );
              })
              .finally(() => {
                setBusyPosting((cur) => (cur === postingNumber ? "" : cur));
              });
          }
        } catch (e) {
          if (cancelled) return;
          setCrptPhase("error");
          setStatusTone("error");
          setStatusMessage(e?.message || "Не удалось привязать код");
        } finally {
          scanInFlightRef.current = false;
          setCrptLoading(false);
          clearScanField();
        }
      })();
    }, debounceMs);

    return () => {
      cancelled = true;
      if (checkTimerRef.current) {
        clearTimeout(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, [scannedText, inputMode, selectedCabinetId, fetchLines, clearScanField]);

  // --- отправка набора, сборка, этикетка ---

  const runForPosting = async (postingNumber, step) => {
    setBusyPosting(postingNumber);
    setStatusMessage(null);
    try {
      if (step === "apply") {
        const res = await ozonAssembliesApi.applyMarking({
          connection_id: Number(selectedCabinetId),
          posting_number: postingNumber,
        });
        setStatusTone(res?.ship_available ? "success" : "warning");
        setStatusMessage(
          res?.ship_available
            ? `Ozon принял ${res.codes_sent} КИЗ по ${postingNumber}. Можно собирать.`
            : `Ozon проверяет коды (статус «${res?.status || "—"}»). Повторите через минуту.`,
        );
      } else if (step === "ship") {
        const products = (postings.find((p) => p.posting_number === postingNumber)?.products || [])
          .map((pr) => ({ sku: pr.sku, quantity: pr.need }));
        await ozonAssembliesApi.ship({
          connection_id: Number(selectedCabinetId),
          posting_number: postingNumber,
          packages: [{ products }],
        });
        setStatusTone("success");
        setStatusMessage(`Отправление ${postingNumber} собрано. Печатайте этикетку.`);
        void fetchLines({ showLoading: false });
      } else if (step === "label") {
        const res = await ozonAssembliesApi.labels({
          connection_id: Number(selectedCabinetId),
          posting_numbers: [postingNumber],
        });
        const url = (res?.file_urls || [])[0] || "";
        if (!url) {
          setStatusTone("warning");
          setStatusMessage("Этикетка ещё готовится — Ozon просит 45–60 секунд. Попробуйте снова.");
          return;
        }
        setLabelUrl(url);
        window.open(url, "_blank", "noopener");
        setStatusTone("success");
        setStatusMessage("Этикетка готова.");
      } else if (step === "unscan") {
        const res = await ozonAssembliesApi.unscan({
          connection_id: Number(selectedCabinetId),
          posting_number: postingNumber,
        });
        setStatusTone("info");
        setStatusMessage(`Снято кодов: ${res?.removed ?? 0}. Товар можно сканировать заново.`);
        void fetchLines({ showLoading: false });
      }
    } catch (e) {
      setStatusTone("error");
      setStatusMessage(e?.message || "Ошибка Ozon");
    } finally {
      setBusyPosting("");
    }
  };

  const scanPipeline = useMemo(() => {
    if (statusTone === "error" && statusMessage) {
      return { active: true, indeterminate: false, value: 100, spin: false, ok: false, err: true };
    }
    if (statusTone === "success" && statusMessage) {
      return { active: true, indeterminate: false, value: 100, spin: false, ok: true, err: false };
    }
    if (crptLoading || crptPhase === "loading") {
      return { active: true, indeterminate: true, value: 50, spin: true, ok: false, err: false };
    }
    if (crptPhase === "debounce") {
      return { active: true, indeterminate: true, value: 18, spin: true, ok: false, err: false };
    }
    return { active: false, indeterminate: true, value: 0, spin: false, ok: false, err: false };
  }, [statusTone, statusMessage, crptLoading, crptPhase]);

  const toggleInputMode = () => {
    setInputMode((m) => (m === "camera" ? "scanner" : "camera"));
    clearScanField();
    setCrptPhase("idle");
  };

  const handleScannerKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = e.target;
      const v = (el.value || "").trim();
      el.value = "";
      if (v) setScannedText(v);
    }
  };

  const handleScannerPaste = (e) => {
    const t = e.clipboardData?.getData("text");
    if (t && String(t).trim()) {
      e.preventDefault();
      setScannedText(String(t).trim());
    }
  };

  const shouldRestoreScannerFocus = () => {
    if (inputModeRef.current !== "scanner") return false;
    const t = lastPointerDownTargetRef.current;
    if (!t || typeof t.closest !== "function") return true;
    return !t.closest(
      "button, a[href], [role='button'], .MuiButtonBase-root, .MuiIconButton-root, input, textarea, select",
    );
  };

  const handleScannerBlur = () => {
    window.setTimeout(() => {
      if (!shouldRestoreScannerFocus()) return;
      focusInputNoScroll(scannerInputRef.current);
    }, 0);
  };

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton
          onClick={() => navigate("/ozon/assemblies", { replace: false })}
          aria-label="Назад"
          sx={barIconSx}
        >
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pr: 0.5,
            minWidth: 0,
            gap: 0.35,
          }}
        >
          <Box sx={{ ...counterPillSx, minWidth: 0, maxWidth: "100%" }}>
            <Typography sx={{ ...counterPillTextSx, textAlign: "center" }}>
              {linesLoading ? "…" : `${remainingUnits} шт`}
            </Typography>
          </Box>
          {selectedCabinetId ? (
            <Box
              sx={cabinetChipSx("ozon")}
              title={selectedCabinetName || `Кабинет ${selectedCabinetId}`}
            >
              <Typography noWrap sx={cabinetChipTextSx("ozon")}>
                {selectedCabinetName || `Кабинет ${selectedCabinetId}`}
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Tooltip
          title={
            inputMode === "camera"
              ? "Ввод со сканера Bluetooth (клавиатурный режим)"
              : "Режим камеры"
          }
        >
          <Box
            component="button"
            type="button"
            onClick={toggleInputMode}
            aria-label={inputMode === "camera" ? "Режим сканера Bluetooth" : "Режим камеры"}
            sx={{
              ...(inputMode === "camera" ? barIconSx : barIconActiveSx),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 0,
              cursor: "pointer",
            }}
          >
            {inputMode === "camera" ? (
              <IconBluetooth size={22} stroke={1.5} />
            ) : (
              <IconCamera size={22} stroke={1.5} />
            )}
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, px: 2, py: 2, overflow: "auto" }}>
        <Box sx={{ ...bannerSx("primary"), mb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={bannerTitleSx("primary")}>СКАНИРУЙТЕ КИЗ</Typography>
            <Typography sx={bannerTextSx}>
              В любом порядке — товар и отправление определятся сами. Первым закрывается то,
              у которого ближе отгрузка.
            </Typography>
          </Box>
        </Box>

        {inputMode === "camera" ? (
          <Box
            sx={{
              height: CAMERA_H,
              borderRadius: `${ds.radiusLg}px`,
              overflow: "hidden",
              bgcolor: camera.bg,
              border: `1px solid ${ds.border2}`,
              boxShadow: ds.shadowXs,
              position: "relative",
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              style={{ width: "100%", height: CAMERA_H, objectFit: "cover", display: "block" }}
            />
            {cameraError && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  bgcolor: camera.overlay,
                }}
              >
                <Typography
                  sx={{ color: camera.errorText, textAlign: "center", fontSize: "0.875rem" }}
                >
                  {cameraError}
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              height: SCANNER_STRIP_H,
              minHeight: SCANNER_STRIP_H,
              maxHeight: SCANNER_STRIP_H,
              flexShrink: 0,
              boxSizing: "border-box",
              borderRadius: `${ds.radiusLg}px`,
              bgcolor: ds.surface2,
              border: `1px solid ${ds.border}`,
              px: 1,
              py: 0.5,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "stretch",
              overflow: "hidden",
            }}
          >
            <TextField
              inputRef={scannerInputRef}
              size="small"
              onKeyDown={handleScannerKeyDown}
              onPaste={handleScannerPaste}
              onBlur={handleScannerBlur}
              placeholder="Код маркировки ЧЗ…"
              fullWidth
              name="bt-scanner-capture"
              inputProps={{
                inputMode: "none",
                enterKeyHint: "done",
                autoCapitalize: "off",
                autoComplete: "off",
                autoCorrect: "off",
                spellCheck: false,
                "aria-label": "Поле ввода кода со сканера Bluetooth",
              }}
              sx={scannerFieldSx}
            />
          </Box>
        )}

        {inputMode === "camera" && decoderError && (
          <Typography sx={{ color: dsText.danger, fontSize: "0.8rem", mt: 1.5 }}>
            {decoderError}
          </Typography>
        )}

        {scanPipeline.active && (
          <Box sx={{ ...cardSx, mt: 1.5, px: 1.6, py: 1.4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <LinearProgress
                  variant={scanPipeline.indeterminate ? "indeterminate" : "determinate"}
                  value={scanPipeline.indeterminate ? 0 : scanPipeline.value}
                  sx={{
                    height: 6,
                    bgcolor: ds.surface3,
                    "& .MuiLinearProgress-bar": {
                      bgcolor: scanPipeline.err
                        ? ds.danger
                        : scanPipeline.ok
                          ? ds.success
                          : ds.primary,
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {scanPipeline.spin ? (
                  <CircularProgress size={18} sx={{ color: ds.primary }} />
                ) : scanPipeline.ok ? (
                  <IconCheck size={20} stroke={2} color={ds.success} />
                ) : scanPipeline.err ? (
                  <IconX size={20} stroke={2} color={ds.danger} />
                ) : null}
              </Box>
            </Box>
            {statusMessage ? (
              <Typography
                sx={{
                  mt: 1,
                  fontSize: "0.8125rem",
                  lineHeight: 1.4,
                  fontWeight: 600,
                  color:
                    statusTone === "error"
                      ? dsText.danger
                      : statusTone === "success"
                        ? dsText.success
                        : statusTone === "warning"
                          ? dsText.warning
                          : ds.text,
                }}
              >
                {statusMessage}
              </Typography>
            ) : null}
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <ManufacturerCatalogPanel
            groupByManufacturer={groupByManufacturer}
            onGroupByManufacturerChange={setGroupByManufacturer}
            catalogStack={catalogStack}
            onCatalogBreadcrumbClick={handleCatalogBreadcrumbClick}
            visibleCatalogs={visibleCatalogs}
            groups={catalogProducts}
            catalogsIndex={catalogsIndex}
            currentCatalogId={currentCatalogId}
            filteredIncomplete={filteredIncomplete}
            filteredComplete={filteredComplete}
            onEnterCatalog={handleEnterCatalog}
            linesLoading={linesLoading}
            linesError={linesError}
            renderProductCard={(g, variant) => (
              <CatalogProductCard key={`${g.key}-${variant}`} product={g} variant={variant} />
            )}
            defaultListEmpty={
              <>
                <Typography sx={{ ...sectionLabelSx, mb: 0.75 }}>
                  ОТПРАВЛЕНИЯ К СБОРКЕ{postings.length ? ` · ${postings.length}` : ""}
                  {readyPostings.length ? ` · готово ${readyPostings.length}` : ""}
                </Typography>

                {linesLoading && !postings.length ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress size={34} sx={{ color: ds.primary }} />
                  </Box>
                ) : null}
                {linesError ? (
                  <Typography sx={{ color: dsText.danger, textAlign: "center", py: 3 }}>
                    {linesError}
                  </Typography>
                ) : null}
                {!linesLoading && !linesError && postings.length === 0 ? (
                  <Typography sx={{ color: ds.text2, textAlign: "center", py: 4 }}>
                    Нет отправлений к сборке
                  </Typography>
                ) : null}

                {postings.map((p) => {
          const complete = p.need > 0 && p.done >= p.need;
          const busy = busyPosting === p.posting_number;
          const highlighted = lastPosting === p.posting_number;
          return (
            <Box
              key={p.posting_number}
              sx={{
                ...(complete ? cardDoneSx : highlighted ? cardActiveSx : cardSx),
                mb: 1,
                py: 1.1,
                px: 1.4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 0.75,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      color: ds.text,
                      fontSize: "0.8125rem",
                      fontWeight: 700,
                      lineHeight: 1.3,
                    }}
                  >
                    {p.posting_number}
                  </Typography>
                  <Typography sx={{ ...mutedTextSx, mt: 0.25 }}>
                    {[orderedLabel(p.ordered_at), shipmentLabel(p.shipment_date)]
                      .filter(Boolean)
                      .join(" · ")}
                    {p.products.length > 1 ? ` · товаров ${p.products.length}` : ""}
                  </Typography>
                </Box>
                <Chip
                  label={complete ? "Готово" : `${p.done} из ${p.need}`}
                  size="small"
                  sx={softChipSx(complete ? "success" : "primary")}
                />
              </Box>

              <Box sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.35 }}>
                {p.products.map((pr) => (
                  <Box
                    key={`${p.posting_number}-${pr.sku}`}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, minWidth: 0 }}
                  >
                    <IconPackages
                      size={14}
                      stroke={1.75}
                      color={ds.text3}
                      aria-hidden
                      style={{ flexShrink: 0, marginTop: 2 }}
                    />
                    {/* Название показываем целиком: кладовщику надо отличать вкусы
                        и граммовки, а они как раз в хвосте строки. */}
                    <Typography
                      sx={{
                        ...mutedTextSx,
                        flex: 1,
                        minWidth: 0,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {pr.title || `SKU ${pr.sku}`}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        fontFeatureSettings: '"tnum"',
                        flexShrink: 0,
                        color: pr.done >= pr.need ? dsText.success : ds.text2,
                      }}
                    >
                      {pr.done}/{pr.need}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {p.done > 0 ? (
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => runForPosting(p.posting_number, "unscan")}
                  sx={{
                    mt: 0.5,
                    textTransform: "none",
                    fontWeight: 600,
                    color: ds.text3,
                    fontSize: "0.72rem",
                  }}
                >
                  Снять коды
                </Button>
              ) : null}
            </Box>
          );
        })}
              </>
            }
          />
        </Box>
      </Box>
    </Box>
  );
}
